﻿import { useState } from 'react'
import { Page, Layout, BlockStack, Box, InlineStack, Pagination, Text, Card } from '@shopify/polaris'
import { useStoreAudits, useStoreAudit } from '@/hooks/useStoreAudit'
import { useDashboard } from '@/hooks/useDashboard'
import { AuditRunCard } from '@/components/audit/AuditRunCard'
import { TriggerAuditButton } from '@/components/audit/TriggerAuditButton'
import { AuditScheduleConfig } from '@/components/audit/AuditScheduleConfig'
import type { StoreAuditRun } from '@/api/storeAudit'

function ExpandableRun({
  run,
  expanded,
  onToggle,
}: {
  run: StoreAuditRun
  expanded: boolean
  onToggle: () => void
}) {
  const { data: detail } = useStoreAudit(expanded ? run.id : 0)
  const fullRun = expanded && detail?.data ? detail.data : run

  return (
    <div onClick={onToggle} style={{ cursor: 'pointer' }}>
      <AuditRunCard run={fullRun} showResults={expanded} />
    </div>
  )
}

export function AuditsPage() {
  const [page, setPage] = useState(1)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const { data: dashboard } = useDashboard()
  const { data, isLoading } = useStoreAudits(page)

  const runs = data?.data ?? []
  const meta = data?.meta

  return (
    <Page fullWidth
      title="Store Audits"
      subtitle="Run and review SEO, trust, and performance checks for your store"
      primaryAction={<TriggerAuditButton remaining={dashboard?.audits_remaining ?? undefined} />}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {!isLoading && runs.length === 0 && (
              <Card>
                <Text as="p" tone="subdued">
                  No audits yet. Run your first audit to get started.
                </Text>
              </Card>
            )}

            {runs.map((run) => (
              <ExpandableRun
                key={run.id}
                run={run}
                expanded={expandedId === run.id}
                onToggle={() => setExpandedId(expandedId === run.id ? null : run.id)}
              />
            ))}

            {meta && meta.last_page > 1 && (
              <Box paddingBlockStart="200">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => setPage((p) => p - 1)}
                    hasNext={page < meta.last_page}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </BlockStack>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          <AuditScheduleConfig />
        </Layout.Section>
      </Layout>
    </Page>
  )
}
